import type { TableRowData } from 'tableTypes'
import { formatNumberToNumberWithSpecifiedDecimals } from 'utils/commonUtils'
import { sortByLevelAscending, sortByLevelDescending } from 'utils/mtcUtils'
import { MappedDriver, MappedTarget } from 'reportViewer'
import { getFormattedBiomarkerName } from '../utils/driverUtils'

const formatDriverName = (driver: MappedDriver, t: (key: string) => string) => {
    return `${getFormattedBiomarkerName(
        driver.item,
        t
    )} (GBS: ${formatNumberToNumberWithSpecifiedDecimals(driver.level, 2)})`
}

/**
 * Sorts drivers
 *
 * @param {MappedTarget} mappedTarget - Druggable target
 * @returns Sorted array of MappedDriver objects
 */
const sortDrivers = (mappedTarget: MappedTarget): MappedDriver[] => {
    return mappedTarget.level >= 0
        ? sortByLevelDescending<MappedDriver>(mappedTarget.relatedDrivers??[])
        : sortByLevelAscending<MappedDriver>(mappedTarget.relatedDrivers??[])
}

/**
 * Maps a druggable target to a table row entity for target page.
 *
 * @param {MappedTarget} mappedTarget - Druggable target
 * @param {string} pathname - Translation function
 * @param {string => string} t - Translation function
 * @returns Table row entity
 */
export const mappedTargetToTableRowData = (
    mappedTarget: MappedTarget,
    pathname: string,
    idx: number,
    t: (key: string) => string
): TableRowData => {
    const formattedTargetName = getFormattedBiomarkerName(
        mappedTarget.item,
        t,
        {
            testResult: mappedTarget.testResult,
            testType: mappedTarget.testType
        }
    )

    const sortedDrivers = sortDrivers(mappedTarget)

    const formattedDriverNames = sortedDrivers.map((driver) =>
        formatDriverName(driver, t)
    )

    return {
        cells: [
            {
                text: formattedTargetName,
                linkPath: `${pathname}/${idx}`
            },
            {
                text:
                    formattedDriverNames.length !== 0
                        ? formattedDriverNames
                        : t('common:labels.na')
            },
            {
                text: formatNumberToNumberWithSpecifiedDecimals(
                    mappedTarget.level,
                    2
                ),
                isProgressBar: true
            }
        ]
    }
}
